/**
 * One video upload, from the chosen file to a converted video
 * (spec 2026-10-23 §5): browser checks → signed form → transfer with progress
 * → conversion → poll every 3 s → ready or failed, or give up after 15 min.
 *
 * Safe to cancel and safe to leave: `cancel()` and unmount both abort the
 * transfer and stop the polling, and every async step checks that its run is
 * still the current one, so a late answer never changes the screen. The
 * decisions are pure functions in lib/upload-flow.ts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AdminApiError } from '@/providers/admin-api';
import { createUpload, readUploadStatus, sendToStorage, startProcessing, UploadCancelled } from './api';
import { uploadErrorWords } from './lib/error-words';
import type { FinalizedMedia } from './lib/intro-state';
import { pollDecision, pollErrorDecision, type PollStep, type UploadState } from './lib/upload-flow';
import { checkVideoFile, readVideoMeta } from './lib/video-client-checks';

export const CANNOT_READ_NOTE =
  'This browser cannot open the file to check it. The server checks it after the upload.';

export type { UploadState };

export function isBusy(s: UploadState): boolean {
  return s.phase === 'checking' || s.phase === 'uploading' || s.phase === 'processing';
}

export function useVideoUpload(apiUrl: string, onReady: (media: FinalizedMedia) => void) {
  const [state, setState] = useState<UploadState>({ phase: 'idle' });
  const run = useRef(0);
  const abortTransfer = useRef<(() => void) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFile = useRef<File | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const stop = useCallback(() => {
    run.current += 1;
    abortTransfer.current?.();
    abortTransfer.current = null;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(
    async (file: File) => {
      stop();
      lastFile.current = file;
      const id = run.current;
      const live = () => run.current === id;
      const fileName = file.name;
      setState({ phase: 'checking', fileName });

      const meta = await readVideoMeta(file);
      if (!live()) return;
      const check = checkVideoFile(file, meta);
      const warnings = meta ? check.warnings : [...check.warnings, CANNOT_READ_NOTE];
      if (check.errors.length > 0) {
        setState({ phase: 'rejected', fileName, errors: check.errors, warnings: check.warnings });
        return;
      }

      try {
        const created = await createUpload(apiUrl, file);
        if (!live()) return;
        setState({ phase: 'uploading', fileName, loaded: 0, total: file.size, warnings });
        const transfer = sendToStorage(created, file, (loaded, total) => {
          if (live()) setState({ phase: 'uploading', fileName, loaded, total, warnings });
        });
        abortTransfer.current = transfer.abort;
        await transfer.done;
        abortTransfer.current = null;
        if (!live()) return;

        setState({ phase: 'processing', fileName, percent: null, warnings });
        const startedAt = Date.now();
        const apply = (step: PollStep) => {
          if (!live()) return;
          if (step.kind === 'ready') {
            setState({ phase: 'done', fileName, warnings });
            onReadyRef.current(step.media);
          } else if (step.kind === 'failed') {
            setState({ phase: 'failed', fileName, error: step.error, retryable: step.retryable, warnings });
          } else {
            setState({ phase: 'processing', fileName, percent: step.percent, warnings });
            timer.current = setTimeout(() => void poll(), step.delayMs);
          }
        };
        const poll = async () => {
          try {
            const status = await readUploadStatus(apiUrl, created.upload_id);
            apply(pollDecision(status, startedAt, Date.now()));
          } catch (e) {
            const status = e instanceof AdminApiError ? e.status : undefined;
            // Plain words (R17): never a raw session, HTTP or validator message.
            apply(pollErrorDecision({ status, message: uploadErrorWords(e) }, startedAt, Date.now()));
          }
        };
        apply(pollDecision(await startProcessing(apiUrl, created.upload_id), startedAt, Date.now()));
      } catch (e) {
        if (!live() || e instanceof UploadCancelled) return;
        setState({ phase: 'failed', fileName, error: uploadErrorWords(e), retryable: true, warnings });
      }
    },
    [apiUrl, stop],
  );

  const cancel = useCallback(() => {
    stop();
    setState({ phase: 'idle' });
  }, [stop]);

  /** The same file again, after a failure. */
  const retry = useCallback(() => {
    if (lastFile.current) void start(lastFile.current);
  }, [start]);

  return { state, start, cancel, retry, busy: isBusy(state) };
}
